import { useState, useEffect } from 'react';
import {
  Card,
  CardBody,
  Skeleton,
  Select,
  SelectItem,
} from '@nextui-org/react';
import useDataStore from '../../../../common/src/stores/useDataStore';
import useFilterStore from '../../../../common/src/stores/useFiltersStore';
import useUserStore from '../../../../common/src/stores/useUserStore';
import {
  DashboardFobMetrics,
  getDashboardFobMetrics,
} from 'common/src/api/settlements';
import { useTranslation } from '../../../../common/src/hooks/useTranslation';
import { KpiCard } from '../ui';

interface MetricCardProps {
  title: string;
  value: string;
  subtitle: string;
  isLoading: boolean;
}

const MetricCard = ({ title, value, subtitle, isLoading }: MetricCardProps) => {
  if (isLoading) {
    return (
      <Card shadow='sm'>
        <CardBody className='p-4'>
          <Skeleton className='h-4 w-2/3 rounded-lg mb-2' />
          <Skeleton className='h-8 w-full rounded-lg mb-2' />
          <Skeleton className='h-3 w-1/2 rounded-lg' />
        </CardBody>
      </Card>
    );
  }

  return <KpiCard label={title} value={value} detail={subtitle} />;
};

const FOBMetricsWidget = () => {
  const { t } = useTranslation('dashboard');
  const { selectedSpecies } = useDataStore();
  const { selectedSeason } = useFilterStore();
  const { userProfile } = useUserStore();
  const [loading, setLoading] = useState(true);
  const [metricsData, setMetricsData] = useState<DashboardFobMetrics | null>(
    null
  );
  const [selectedMarket, setSelectedMarket] = useState<string>('all');
  const [selectedVariety, setSelectedVariety] = useState<string>('all');

  // Reset filters when season or species changes
  useEffect(() => {
    setSelectedMarket('all');
    setSelectedVariety('all');
  }, [selectedSeason, selectedSpecies?.id]);

  // Fetch detail-derived dashboard metrics
  useEffect(() => {
    if (!selectedSpecies?.id || !selectedSeason || !userProfile?.exporter_id) {
      setMetricsData(null);
      setLoading(false);
      return;
    }

    const fetchMetrics = async () => {
      setLoading(true);
      try {
        const data = await getDashboardFobMetrics(
          userProfile.exporter_id,
          selectedSeason,
          selectedSpecies.id,
          selectedMarket,
          selectedVariety
        );
        setMetricsData(data);
      } catch (error) {
        console.error('Error fetching FOB metrics:', error);
        setMetricsData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchMetrics();
  }, [
    selectedSpecies?.id,
    selectedSeason,
    userProfile?.exporter_id,
    selectedMarket,
    selectedVariety,
  ]);

  const markets = metricsData?.markets || [];
  const varieties = metricsData?.varieties || [];
  const unitWeight = Number(selectedSpecies?.default_unit_price) || 5;
  const totalFob = metricsData?.total_fob || 0;
  const totalKilos = metricsData?.total_kilos || 0;
  const metrics = {
    fobTotal: totalFob,
    fobCajaEquivalente:
      totalKilos > 0 ? (totalFob / totalKilos) * unitWeight : 0,
    totalKilos,
    unitWeight,
    settlementsCount: metricsData?.settlement_count || 0,
  };

  // Format currency
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatCurrencyDecimal = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  return (
    <div>
      {/* Header */}

      {/* Filters */}
      <div className='flex flex-col md:flex-row gap-4 mb-4'>
        <Select
          label={t('Mercado')}
          selectedKeys={selectedMarket ? [selectedMarket] : []}
          onChange={(e) => setSelectedMarket(e.target.value || 'all')}
          className='w-full md:w-64'
          size='sm'
          aria-label='Selecciona mercado'
          isDisabled={loading}
        >
          <SelectItem key='all' value='all'>
            {t('Todos')}
          </SelectItem>
          {markets.map((market) => (
            <SelectItem key={market} value={market}>
              {market}
            </SelectItem>
          ))}
        </Select>

        <Select
          label={t('Variedad')}
          selectedKeys={selectedVariety ? [selectedVariety] : []}
          onChange={(e) => setSelectedVariety(e.target.value || 'all')}
          className='w-full md:w-64'
          size='sm'
          aria-label='Selecciona variedad'
          isDisabled={loading}
        >
          <SelectItem key='all' value='all'>
            {t('Todas')}
          </SelectItem>
          {varieties.map((variety) => (
            <SelectItem key={variety} value={variety}>
              {variety}
            </SelectItem>
          ))}
        </Select>
      </div>

      {/* Metrics Cards */}
      <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
        <MetricCard
          title={t('FOB Total')}
          value={formatCurrency(metrics.fobTotal)}
          subtitle={`${metrics.settlementsCount} ${t('liquidaciones aprobadas')}`}
          isLoading={loading}
        />

        <MetricCard
          title={t('FOB Caja Equivalente')}
          value={formatCurrencyDecimal(metrics.fobCajaEquivalente)}
          subtitle={`${t('Por caja de')} ${metrics.unitWeight}kg`}
          isLoading={loading}
        />

        {/* <MetricCard
          title='Retorno Productor'
          value='—'
          subtitle='Próximamente'
          isLoading={loading}
        /> */}
      </div>
    </div>
  );
};

export default FOBMetricsWidget;
